import { useState } from "react";
import { proposePriceEdit, type PriceEditProposal } from "./actions";

// Propose a price edit for one product (ops/PRODUCT_EDIT_WORKFLOW_SCOPE.md). Proposing writes a reviewable record to
// `pending_product_edits` only: it NEVER mutates the product document (the corpus is read-only) and NEVER touches WooCommerce.
// See the Pending Edits tab for what happens to a proposal next. Opened from the Sheet's row preview.

function money(n: number | null) {
  return n == null ? "—" : n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function PriceEditForm({ product, onClose, onProposed }: {
  product: { docId: string; sku: string; price: number | null }; onClose: () => void; onProposed: (p: PriceEditProposal) => void;
}) {
  const [price, setPrice] = useState(product.price != null ? String(product.price) : "");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PriceEditProposal | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const num = Number(price);
    if (!Number.isFinite(num) || num < 0) { setError("Enter a valid, non-negative price"); return; }
    setSubmitting(true);
    setError(null);
    const r = await proposePriceEdit(product.docId, num);
    setSubmitting(false);
    if (!r.ok) { setError(r.error); return; }
    setResult(r.data);
    onProposed(r.data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>Propose price edit — {product.sku}</h3>
        <p className="meta">Current: {money(product.price)}. This only writes a proposal for review — nothing live changes.</p>

        {!result && (
          <>
            <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
                   style={{ width: "100%", marginBottom: 8 }} />
            {error && <div className="panel error">{error}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={submit} disabled={submitting}>{submitting ? "Checking…" : "Propose"}</button>
              <button onClick={onClose} disabled={submitting}>Cancel</button>
            </div>
          </>
        )}

        {result && (
          <div>
            {result.duplicateGroupBlocked && (
              <div className="panel error">
                🔴 Blocked: this SKU has multiple duplicate WooCommerce listings — resolve via
                Data Health first. Proposal recorded as blocked, not silently applied to one copy.
              </div>
            )}
            {!result.duplicateGroupBlocked && result.priceMapClamped && (
              <div className="panel error">
                ⚠️ Your proposed price (${result.priceProposed}) was below the real MAP floor
                (${result.mapPriceAtProposal}) — clamped to ${result.priceFinal}, never allowed below MAP.
              </div>
            )}
            {!result.duplicateGroupBlocked && !result.priceMapClamped && (
              <p>Proposed: {money(result.priceFinal)} — see the Pending Edits tab.</p>
            )}
            <button onClick={onClose} style={{ marginTop: 8 }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

